import React, { useContext, useEffect, useRef, useState } from "react";
import "./styles.scss";
import Box from "Components/Atoms/Box";
import Modal from "../../Molecules/Modal";
import { Backspace, Close } from "@material-ui/icons";
import { Signature, SignatureContext } from "../../../Contexts/SignatureContext";
import TextField from "../../Atoms/TextField";
import Typography, { Colors } from "../../Atoms/Typography";

// The PIN pad of the waiter signatures. Every PIN is checked by the server
// (/v03/signatures, counted per account); this screen never sees a verifier.
// "edit": the current PIN is checked first and then sent along with the new
// one, because the server authorises the change itself.

type PinCodeModalProps = {
  open: boolean;
  onClose: () => Promise<void> | void;
  error?: null | string;
  type?: "validate" | "new" | "edit";
  signatureModalData: Signature;
};

type PincodeMessage = {
  message?: string;
  color?: Colors;
};

const PincodeModal = ({
  open,
  onClose,
  type,
  error,
  signatureModalData,
}: PinCodeModalProps) => {
  const {
    signatures,
    pincodeModalData,
    setPincodeModalData,
    addSignature,
    changeSignature,
    verifyPin,
    promptToken,
    isCurrentPrompt,
  } = useContext(SignatureContext);
  //used to hold first pincode entry
  const [firstPincodeEntry, setFirstPincodeEntry] = useState<string>("");
  const [pincodeInput, setPincodeInput] = useState<string>("");
  const [pincodeMessage, setPincodeMessage] = useState<PincodeMessage>({});
  // The current PIN of an "edit", accepted by the server; sent again with the new one.
  const currentPin = useRef<string | undefined>(undefined);
  const checking = useRef(false);
  const messageAfterTypeChange = useRef<PincodeMessage | null>(null);

  // A closed prompt keeps nothing for the next one (also after a restaurant switch).
  useEffect(() => {
    if (open) return;
    currentPin.current = undefined;
    setFirstPincodeEntry("");
    setPincodeInput("");
    setPincodeMessage({});
  }, [open]);

  function pincodeInputHandler(value: string) {
    if (pincodeInput.length < 4) setPincodeInput((prev) => prev + value);
  }
  function backspaceHandler() {
    setPincodeInput((prev) => prev.substring(0, prev.length - 1));
  }

  /**
   * The server's answer to this PIN for THIS prompt: accepted, or refused (shown here).
   * An answer for a prompt that changed or closed meanwhile (other restaurant, other
   * Kürzel, closed) is dropped: nothing shown, no callback, nothing reopened.
   */
  async function serverAccepts(pin: string): Promise<"accepted" | "refused" | "stale"> {
    if (checking.current) return "stale";
    checking.current = true;
    const token = promptToken();
    try {
      const refusal = await verifyPin(signatureModalData.signature, pin);
      if (!isCurrentPrompt(token)) return "stale";
      if (!refusal) return "accepted";
      setPincodeMessage({ message: refusal.message, color: "error" });
      setPincodeInput("");
      return "refused";
    } finally {
      checking.current = false;
    }
  }

  async function editPincode(pin: string) {
    if ((await serverAccepts(pin)) === "accepted") {
      currentPin.current = pin;
      // The accepted current PIN is not the first entry of the new one: the pad starts empty.
      setPincodeInput("");
      setFirstPincodeEntry("");
      messageAfterTypeChange.current = {
        message: "Valid pin. Enter new pin.",
        color: "secondary",
      };
      setPincodeModalData({ ...pincodeModalData, type: "new" });
    }
  }

  async function pinCheckOnValidate(pin: string) {
    if ((await serverAccepts(pin)) === "accepted") {
      setPincodeMessage({ message: "Good pin.", color: "primary" });
      const onValid = pincodeModalData.onValidCode;
      setPincodeModalData({ open: false });
      onValid?.(pin);
    }
  }

  async function saveNewPin(pin: string) {
    const token = promptToken();
    const saved = !signatures.find((s) => s.signature === signatureModalData.signature)
      ? await addSignature({ signature: signatureModalData.signature, name: signatureModalData.name, pin })
      : await changeSignature(signatureModalData.signature, { pin, currentPin: currentPin.current });
    if (!saved && isCurrentPrompt(token)) {
      setFirstPincodeEntry("");
      setPincodeInput("");
    }
  }

  function closeModalHandler() {
    onClose();
  }

  useEffect(() => {
    if (type === "new") {
      //new pincode flow
      if (pincodeInput.length === 4 && firstPincodeEntry.length !== 4) {
        setFirstPincodeEntry(pincodeInput);
        setPincodeInput("");
        setPincodeMessage({ message: "Confirm pin.", color: "secondary" });
      }
      if (
        pincodeInput.length === 4 &&
        firstPincodeEntry.length === 4 &&
        pincodeInput === firstPincodeEntry
      ) {
        saveNewPin(pincodeInput);
        setPincodeMessage({});
      }
      if (
        pincodeInput.length === 4 &&
        firstPincodeEntry.length === 4 &&
        pincodeInput !== firstPincodeEntry
      ) {
        setPincodeMessage({
          message: "PINs not matching. Try again.",
          color: "error",
        });
        setPincodeInput("");
      }
    }

    if (type === "edit" && pincodeInput.length === 4) {
      editPincode(pincodeInput);
    }
    if (type === "validate" && pincodeInput.length === 4) {
      pinCheckOnValidate(pincodeInput);
    }
  }, [pincodeInput, type]);

  // A new step starts empty; it may carry one message from the step before (old PIN accepted).
  useEffect(() => {
    setFirstPincodeEntry("");
    setPincodeInput("");
    setPincodeMessage(messageAfterTypeChange.current ?? {});
    messageAfterTypeChange.current = null;
  }, [type]);

  return (
    <Modal
      open={open}
      onClose={closeModalHandler}
      title={type === "new" ? "Enter new pin code" : "Enter your pin code"}
      titleTranslation="settings"
      headerRight={<Close onClick={onClose} />}
    >
      <Box className="pincode-modal-container">
        <Box className="pincode-modal-input-container">
          <TextField
            fullWidth
            autoFocus
            disabled={!!("ontouchstart" in document.documentElement)}
            type={type === "validate" ? "password" : "number"}
            hidePasswordIcon
            value={pincodeInput}
            onChange={(e) => setPincodeInput(e.target.value.slice(0, 4))}
            inputProps={{
              pattern: "[0-9]*",
              min: 0,
              max: 4,
              style: { textAlign: "center", fontSize: 20, fontWeight: "bold" },
            }}
          />
          <Backspace
            onClick={backspaceHandler}
            style={{ position: "absolute", right: 8, top: 8 }}
          />
          {error ||
            (pincodeMessage?.message && (
              <Box className="pincode-error-container">
                <Typography
                  variant="text-3"
                  translation="settings"
                  color={pincodeMessage?.color ?? "subdued"}
                >
                  {pincodeMessage?.message}
                </Typography>
              </Box>
            ))}
        </Box>
        <Box className="pincode-modal-digits-container">
          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 0].map((digit) => (
            <Box
              key={digit}
              className={digit !== 0 ? "digit-container" : "zero-digit"}
              onClick={() => pincodeInputHandler(digit.toString())}
            >
              <Typography variant="h3" textAlign="center" translation={null}>
                {digit}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Modal>
  );
};

export default PincodeModal;
